/**
 * Custom Agent Node Component
 *
 * Renders different node types for the visual architect
 */
import { memo } from 'react'
import { Handle, Position, NodeProps } from '@xyflow/react'
import { clsx } from 'clsx'
import {
  SparklesIcon,
  UserGroupIcon,
  ArrowsPointingOutIcon,
  Square3Stack3DIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowRightEndOnRectangleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  ChartBarIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  BeakerIcon,
  BookOpenIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'
import { AgentNodeData, AgentNodeType } from '@/types/architect'

const nodeIcons: Record<AgentNodeType, React.ElementType> = {
  input: ArrowRightStartOnRectangleIcon,
  output: ArrowRightEndOnRectangleIcon,
  specialist: SparklesIcon,
  supervisor: UserGroupIcon,
  router: ArrowsPointingOutIcon,
  aggregator: Square3Stack3DIcon,
}

const specialistIcons: Record<string, React.ElementType> = {
  coding: CodeBracketIcon,
  documents: DocumentTextIcon,
  graphs: ChartBarIcon,
  spreadsheets: TableCellsIcon,
  presentations: PresentationChartBarIcon,
  data_analysis: BeakerIcon,
  literature_review: BookOpenIcon,
  general: CpuChipIcon,
}

const nodeColors: Record<AgentNodeType, { bg: string; border: string; icon: string; text: string }> = {
  input: { bg: 'bg-green-500/15 dark:bg-green-900/60', border: 'border-green-500', icon: 'text-green-600 dark:text-green-400', text: 'text-green-600 dark:text-green-100' },
  output: { bg: 'bg-red-500/15 dark:bg-red-900/60', border: 'border-red-500', icon: 'text-red-600 dark:text-red-400', text: 'text-red-600 dark:text-red-100' },
  specialist: { bg: 'bg-violet-100 dark:bg-violet-900/60', border: 'border-violet-500', icon: 'text-violet-600 dark:text-violet-400', text: 'text-violet-900 dark:text-violet-100' },
  supervisor: { bg: 'bg-purple-500/15 dark:bg-purple-900/60', border: 'border-purple-500', icon: 'text-purple-600 dark:text-purple-400', text: 'text-purple-600 dark:text-purple-100' },
  router: { bg: 'bg-amber-500/15 dark:bg-yellow-900/60', border: 'border-amber-500', icon: 'text-amber-600 dark:text-yellow-400', text: 'text-amber-600 dark:text-yellow-100' },
  aggregator: { bg: 'bg-blue-500/15 dark:bg-blue-900/60', border: 'border-blue-500', icon: 'text-blue-600 dark:text-blue-400', text: 'text-blue-600 dark:text-blue-100' },
}

interface AgentNodeComponentProps extends NodeProps<AgentNodeData> {
  onConfigure?: (nodeId: string) => void
}

function AgentNodeComponent(props: AgentNodeComponentProps) {
  const { data, selected } = props
  const colors = nodeColors[data.nodeType]
  const NodeIcon = data.nodeType === 'specialist' && data.specialistType
    ? specialistIcons[data.specialistType] || SparklesIcon
    : nodeIcons[data.nodeType]

  const showSourceHandle = data.nodeType !== 'output'
  const showTargetHandle = data.nodeType !== 'input'
  const showMultipleOutputs = data.nodeType === 'router' || data.nodeType === 'supervisor'

  return (
    <div
      className={clsx(
        'px-2 py-1 rounded border min-w-[90px] max-w-[140px] transition-all text-[11px]',
        colors.bg,
        selected ? 'border-white shadow-lg shadow-white/20' : colors.border,
        'hover:shadow-lg'
      )}
    >
      {/* Target Handle */}
      {showTargetHandle && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-2 !h-2 !bg-muted-foreground !border !border-border"
        />
      )}

      {/* Content */}
      <div className="flex items-center gap-1.5">
        <div className={clsx('p-1 rounded bg-gray-800/50', colors.icon)}>
          <NodeIcon className="w-3 h-3" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className={clsx('font-medium text-[11px] truncate', colors.text)}>{data.label}</span>
            {data.isConfigured ? (
              <CheckCircleIcon className="w-3 h-3 text-green-400 flex-shrink-0" />
            ) : (
              <ExclamationTriangleIcon className="w-3 h-3 text-yellow-400 flex-shrink-0" />
            )}
          </div>
          {data.specialistType && (
            <span className="text-xs text-muted-foreground capitalize">
              {data.specialistType.replace('_', ' ')}
            </span>
          )}
          {data.nodeType === 'aggregator' && data.aggregationStrategy && (
            <span className="text-xs text-muted-foreground capitalize">
              {data.aggregationStrategy}
            </span>
          )}
          {data.providerMode && (
            <span className={clsx(
              'inline-block mt-1 text-xs px-1.5 py-0.5 rounded',
              data.providerMode === 'local' ? 'bg-green-900/50 text-green-400' :
              data.providerMode === 'api' ? 'bg-blue-900/50 text-blue-400' :
              data.providerMode === 'hybrid' ? 'bg-purple-900/50 text-purple-400' :
              'bg-yellow-900/50 text-yellow-400'
            )}>
              {data.providerMode}
            </span>
          )}
        </div>
      </div>

      {/* Source Handle(s) */}
      {showSourceHandle && (
        <>
          {showMultipleOutputs ? (
            <>
              <Handle
                type="source"
                position={Position.Right}
                id="a"
                style={{ top: '30%' }}
                className="!w-2 !h-2 !bg-muted-foreground !border !border-border"
              />
              <Handle
                type="source"
                position={Position.Right}
                id="b"
                style={{ top: '50%' }}
                className="!w-2 !h-2 !bg-muted-foreground !border !border-border"
              />
              <Handle
                type="source"
                position={Position.Right}
                id="c"
                style={{ top: '70%' }}
                className="!w-2 !h-2 !bg-muted-foreground !border !border-border"
              />
            </>
          ) : (
            <Handle
              type="source"
              position={Position.Right}
              className="!w-2 !h-2 !bg-muted-foreground !border !border-border"
            />
          )}
        </>
      )}
    </div>
  )
}

export default memo(AgentNodeComponent)
